import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface EmailVerificationFormProps {
  userId: number;
  email: string;
  onSuccess: () => void;
  onBack: () => void;
}

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({ 
  userId, 
  email, 
  onSuccess, 
  onBack 
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setLoading(true);

    try {
      const response = await apiService.verifyEmail(userId, code);
      if (response.token && response.user) {
        // Email verification successful - proceed to login flow
        onSuccess();
      }
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    
    try {
      await apiService.resendVerification(userId);
      setTimeLeft(600); // Reset timer
      setCanResend(false);
    } catch (error) {
      console.error('Resend failed:', error);
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div className="auth-form">
      <h2>Verify Your Email</h2>
      
      <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
        <p style={{ color: '#666', marginBottom: '0.5rem' }}>
          We sent a verification code to:
        </p>
        <p style={{ fontWeight: 'bold', color: '#333' }}>
          {email}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="code">6-Digit Verification Code:</label>
          <input
            type="text"
            id="code"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            maxLength={6}
            pattern="[0-9]{6}"
            required
            disabled={loading}
            style={{
              textAlign: 'center',
              fontSize: '1.2rem',
              letterSpacing: '0.2rem',
              fontFamily: 'monospace'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem', textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
          {timeLeft > 0 ? (
            <span>Code expires in: <strong>{formatTime(timeLeft)}</strong></span>
          ) : (
            <span style={{ color: '#f44336' }}>Code has expired</span>
          )}
        </div>

        <button 
          type="submit" 
          disabled={loading || code.length !== 6 || timeLeft === 0}
          style={{ marginBottom: '1rem' }}
        >
          {loading ? 'Verifying...' : 'Verify Email'}
        </button>
      </form>

      <div style={{ textAlign: 'center' }}>
        <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Didn't receive the code?
        </p>
        
        <button
          type="button"
          className="link-button"
          onClick={handleResend}
          disabled={resending || !canResend}
          style={{ marginRight: '1rem' }}
        >
          {resending ? 'Sending...' : 'Resend Code'}
        </button>

        <button
          type="button"
          className="link-button"
          onClick={onBack}
          disabled={loading || resending}
        >
          Back to Registration
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationForm;